import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs/operators';
import { UserRightsStore } from '@core/user';
import { RootRoutes } from '@core/navigation';
import { AuthStore } from '@core/auth';

export const impersonationGuard: CanActivateFn = () => {
  const userRightsStore = inject(UserRightsStore);
  const authStore = inject(AuthStore);
  const router = inject(Router);

  // Always allow access if currently impersonating (to allow stopping impersonation)
  if (authStore.isImpersonated()) {
    return true;
  }

  const currentStatus = userRightsStore.getUserRightsCallStatus();

  if (currentStatus === 'loaded') {
    const hasPermission = userRightsStore.canImpersonate();

    if (!hasPermission) {
      router.navigate(RootRoutes.root);
    }

    return hasPermission;
  }

  if (typeof currentStatus === 'object' && 'error' in currentStatus) {
    router.navigate(RootRoutes.root);
    return false;
  }

  // Trigger loading if not yet started
  if (currentStatus === 'init') {
    userRightsStore.getUserRights();
  }

  return toObservable(userRightsStore.getUserRightsCallStatus).pipe(
    filter(
      (status) =>
        status === 'loaded' || (typeof status === 'object' && 'error' in status)
    ),
    take(1),
    map(() => {
      const hasPermission = userRightsStore.canImpersonate();

      if (!hasPermission) {
        router.navigate(RootRoutes.root);
      }

      return hasPermission;
    })
  );
};
